import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MainLayout } from "components/layout/MainLayout";
import {
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Input,
  Select,
  Button,
} from "components/ui";
import { lookupService } from "services/lookup.service";
import { useToast } from "hooks/useToast";
import { ROUTES } from "constants/index";
import { useFieldValidation } from "hooks/useFieldValidation";

interface LookupOption {
  value: string | number;
  label: string;
}

export const LookupFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = Boolean(id) && !location.pathname.endsWith("/view");
  const isView = location.pathname.endsWith("/view");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const [lookups, setLookups] = useState<LookupOption[]>([]);

  const [form, setForm] = useState({
    code: "",
    valueEn: "",
    valueAr: "",
    parentId: "",
    valueEnId: 0 as number | 0,
    valueArId: 0 as number | 0,
  });

  const validators = useMemo(
    () => ({
      code: (state: typeof form) =>
        !state.code?.trim() ? "Code is required" : undefined,
      parentId: (state: typeof form) =>
        !state.parentId ? "Lookup parent code is required" : undefined,
    }),
    []
  );

  const { errors, validateAll, revalidateFields, resetErrors } =
    useFieldValidation<typeof form>(validators);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      revalidateFields(next, [name]);
      return next;
    });
  };

  const handleLookupChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    setForm((prev) => {
      const next = { ...prev, parentId: String(e.target.value) };
      revalidateFields(next, ["parentId"]);
      return next;
    });
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const items = await lookupService.getAll();
        setLookups(
          (items || [])
            .filter((c: any) => c.parentId === null)
            .map((c: any) => ({
              value: c.id,
              label: `${c.code}`,
            }))
        );

        if ((isEdit || isView) && id) {
          const lookup = await lookupService.getById(id);

          const enDetail = lookup.lookupDetails?.find(
            (d: any) => d.languageId === 1
          );
          const arDetail = lookup.lookupDetails?.find(
            (d: any) => d.languageId === 2
          );
          setForm({
            code: (lookup as any).code || "",
            valueEn: enDetail?.name || "",
            valueAr: arDetail?.name || "",
            valueEnId: enDetail?.id ?? 0,
            valueArId: arDetail?.id ?? 0,
            parentId: (lookup as any).parentId || null,
          });
          resetErrors();
        }
      } catch (e: any) {
        const friendly =
          "An error occurred on the server. Please contact the administrator.";
        setError(e.message ?? friendly);
        toast.error(e.message ?? friendly);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, isEdit, resetErrors]);

  const onSubmit = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const validationResult = validateAll(form);
      if (Object.keys(validationResult).length > 0) {
        setIsLoading(false);
        return;
      }
      const lookupDetails = [
        {
          id: form.valueEnId,
          name: form.valueEn,
          languageId: 1,
        },
        {
          id: form.valueArId,
          name: form.valueAr,
          languageId: 2,
        },
      ];
      const payload = {
        code: form.code,
        valueEn: form.valueEn,
        valueAr: form.valueAr,
        parentId: form.parentId,
        lookupDetails,
      } as any;

      if (isEdit && id) {
        await lookupService.update(id, payload);
        toast.success("Lookup updated successfully");
      } else {
        await lookupService.create(payload);
        toast.success("Lookup created successfully");
      }
      navigate(ROUTES.LOOKUPS);
    } catch (e: any) {
      const friendly =
        "An error occurred on the server. Please contact the administrator.";
      setError(e.message ?? friendly);
      toast.error(e.message ?? friendly);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader
          title={isEdit ? "Edit Lookup" : isView ? "View Lookup" : "New Lookup"}
        />
        <CardBody>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-6">
            <div className="sm:col-span-8">
              <Grid cols={2} gap="lg">
                <GridItem>
                  <Select
                    label="Parent Code"
                    options={lookups}
                    value={form.parentId}
                    onChange={handleLookupChange}
                    error={errors.parentId}
                    fullWidth
                    required
                    disabled={isEdit}
                  ></Select>
                </GridItem>
                <GridItem>
                  <Input
                    label="Code"
                    name="code"
                    value={form.code}
                    onChange={handleChange}
                    fullWidth
                    error={errors.code}
                    required
                    disabled={isEdit}
                  />
                </GridItem>

                <GridItem>
                  <Input
                    label="Value (En)"
                    name="valueEn"
                    value={form.valueEn}
                    onChange={handleChange}
                    fullWidth
                    error={errors.valueEn}
                  />
                </GridItem>
                <GridItem>
                  <Input
                    label="Value (Ar)"
                    name="valueAr"
                    value={form.valueAr}
                    onChange={handleChange}
                    fullWidth
                    error={errors.valueAr}
                  />
                </GridItem>
              </Grid>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="flex justify-end gap-3 mt-6">
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.LOOKUPS)}
          disabled={isLoading}
        >
          Back
        </Button>
        {!isView && (
          <Button
            variant="primary"
            onClick={onSubmit}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Save
          </Button>
        )}
      </div>
    </MainLayout>
  );
};
